import type { ColumnSizingState, RowData } from "@tanstack/react-table";

import type { DashboardTable } from "@/design-system/Table/helpers";
import {
  type PersistentResizableTableOptions,
  type TableSizingPersistence,
  usePersistentResizableTable,
} from "@/design-system/Table/usePersistentResizableTable";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { getLocalStorageValue, updateLocalStorage } from "@/hooks/useLocalStorage";
import { useConfig } from "@/providers/config/useConfig";

const tableSizingStorage: Omit<TableSizingPersistence, "key"> = {
  read: (key) => getLocalStorageValue<ColumnSizingState>(key, {}),
  write: updateLocalStorage,
};

// Stable identifier per distinct table. Never rename a value because it is part
// of the persisted localStorage key for users' column widths.
export const TABLE_KEYS = {
  ANALYTICS_CONTRIBUTION: "analytics-contribution",
  ANALYTICS_BOOKABLE_ASSETS: "analytics-bookable-assets",
  ANALYTICS_BOOKINGS: "analytics-bookings",
  ANALYTICS_COMMUNITY_FEED_GROUPS: "analytics-community-feed-groups",
  ANALYTICS_EVENTS: "analytics-events",
  ANALYTICS_EVENT_SERIES: "analytics-event-series",
  ANALYTICS_TICKET_CATEGORIES: "analytics-ticket-categories",
  ANALYTICS_TICKETS: "analytics-tickets",
  ADDRESSES: "addresses",
  ALERTS: "alerts",
  ALERT_LIKES_THRESHOLD: "alert-likes-threshold",
  ALERT_COMMENTS_THRESHOLD: "alert-comments-threshold",
  ALERT_POST_KEYWORDS: "alert-post-keywords",
  ALERT_COMMENT_KEYWORDS: "alert-comment-keywords",
  AUTOMATED_SURVEYS: "automated-surveys",
  AUTOMATED_SURVEY_QUEUE: "automated-survey-queue",
  BOOKINGS: "bookings",
  BUILDINGS: "buildings",
  COMMUNITY_INFORMED_ADMINS: "community-informed-admins",
  COMPANIES: "companies",
  DOCUMENTS_FOLDERS: "documents-folders",
  EMAIL_UNSUBSCRIBE: "email-unsubscribe",
  FAH_QUESTION_RESPONSES: "fah-question-responses",
  PLATFORM_GROUPS: "platform-groups",
  PLUS_BUTTON_OPTIONS: "plus-button-options",
  PORTFOLIO_INTEGRATIONS: "portfolio-integrations",
  PORTFOLIO_OVERVIEW_LEGACY: "portfolio-overview-legacy",
  PORTFOLIO_TICKETS: "portfolio-tickets",
  PROJECT_CONNECTIONS: "project-connections",
  PROJECT_CONNECTION_GROUPS: "project-connection-groups",
  ROLES: "roles",
  SURVEYS: "surveys",
  TICKETS_ADMIN: "tickets-admin",
  TICKETS_RESIDENT: "tickets-resident",
  TICKET_CATEGORIES: "ticket-categories",
  TICKET_STATUSES: "ticket-statuses",
  USERS: "users",
} as const;

export type TableType = (typeof TABLE_KEYS)[keyof typeof TABLE_KEYS];

/**
 * Enables column resizing and persists widths per table type and user. Widths are
 * shared across projects because the storage key intentionally omits the project id.
 */
export function useResizableTable<TData extends RowData>(
  tableType: TableType,
  options: PersistentResizableTableOptions<TData>,
): DashboardTable<TData> {
  const envLongName = useConfig("envLongName");
  const sessionUser = useSessionUser();
  const storageKey = `${envLongName}.${sessionUser.id}.table-column-sizing.${tableType}`;

  return usePersistentResizableTable(options, { ...tableSizingStorage, key: storageKey });
}
